import countryCentroids from "../data/countryCentroids.json";

/** Coordinate pair as [latitude, longitude]. */
export type LatLng = [number, number];

/**
 * Map of location names → [lat, lng] pairs.
 * Static fallback loaded from the bundled centroids dataset.
 */
const staticCentroidMap = countryCentroids as unknown as Record<string, LatLng>;

/** Active centroid map — starts with static data, can be replaced by OSDK data. */
let centroidMap: Record<string, LatLng> = staticCentroidMap;
let sortedKeys: string[] = Object.keys(centroidMap).sort(
  (a, b) => b.length - a.length,
);

/**
 * Replace the centroid map with data from the OSDK geo-coord library.
 * Call once after fetching `getGeoCoordLibrary`.
 */
export function setCentroidData(
  locations: Array<{ name: string; lat: number; lng: number }>,
): void {
  const map: Record<string, LatLng> = {};
  for (const loc of locations) {
    map[loc.name] = [loc.lat, loc.lng];
  }
  centroidMap = map;
  sortedKeys = Object.keys(map).sort((a, b) => b.length - a.length);
}

/**
 * Resolve a free-text location string to a [lat, lng] pair.
 *
 * Multi-step algorithm:
 *  1. Exact match (case-insensitive)
 *  2. Substring match — find the longest centroid key contained in the input
 *  3. Comma-split fallback — try each comma-delimited segment
 *
 * Returns `null` when no match is found.
 */
export function resolveLocation(location: string): LatLng | null {
  const trimmed = location.trim();
  if (!trimmed) return null;
  if (/unclear/i.test(trimmed) || /^global$/i.test(trimmed)) return null;

  // 1. Exact match (case-insensitive lookup)
  for (const key of Object.keys(centroidMap)) {
    if (key.toLowerCase() === trimmed.toLowerCase()) {
      return centroidMap[key];
    }
  }

  // 2. Substring match — bidirectional (input contains key, or key contains input)
  const lowerTrimmed = trimmed.toLowerCase();
  for (const key of sortedKeys) {
    const lowerKey = key.toLowerCase();
    if (lowerTrimmed.includes(lowerKey) || lowerKey.includes(lowerTrimmed)) {
      return centroidMap[key];
    }
  }

  // 3. Comma/semicolon-split fallback — try segments right-to-left
  if (/[,;]/.test(trimmed)) {
    const segments = trimmed.split(/[,;]\s*/).map((s) => s.trim());
    for (let i = segments.length - 1; i >= 0; i--) {
      const result = resolveLocation(segments[i]);
      if (result) return result;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Clustering helpers — group blocks by resolved country/region
// ---------------------------------------------------------------------------

export interface LocationCluster {
  /** Display label for the cluster (the matched centroid key). */
  label: string;
  /** Coordinates for the map marker. */
  coords: LatLng;
  /** Block UIDs that belong to this cluster. */
  blockUids: string[];
  /** Unique titles for tooltip display. */
  blockTitles: string[];
  /** First block UID for each unique title (parallel to blockTitles). */
  titleUids: string[];
  /** Location blurbs for tooltip display (parallel to blockTitles). */
  blockBlurbs: string[];
}

export interface LocationInput {
  uid: string;
  location: string;
  title?: string;
  blurb?: string;
}

/**
 * Build location clusters from an array of block location inputs.
 * Blocks that resolve to the same coordinates are merged into one cluster.
 */
export function buildLocationClusters(
  items: LocationInput[],
): LocationCluster[] {
  const clusterMap = new Map<string, LocationCluster>();

  for (const item of items) {
    const coords = resolveLocation(item.location);
    if (!coords) continue;

    // Use coordinate string as key to merge same-location blocks
    const key = `${coords[0]},${coords[1]}`;

    const existing = clusterMap.get(key);
    if (existing) {
      existing.blockUids.push(item.uid);
      if (item.blurb) {
        existing.blockTitles.push(item.title ?? "");
        existing.titleUids.push(item.uid);
        existing.blockBlurbs.push(item.blurb);
      }
    } else {
      // Find the matched label for display
      const label = findMatchedLabel(item.location) || item.location;
      clusterMap.set(key, {
        label,
        coords,
        blockUids: [item.uid],
        blockTitles: item.blurb ? [item.title ?? ""] : [],
        titleUids: item.blurb ? [item.uid] : [],
        blockBlurbs: item.blurb ? [item.blurb] : [],
      });
    }
  }

  return Array.from(clusterMap.values());
}

/**
 * Find the centroid key that matches a location string (for display label).
 */
export function findMatchedLabel(location: string): string | null {
  const trimmed = location.trim();
  if (!trimmed) return null;

  // Exact match
  for (const key of Object.keys(centroidMap)) {
    if (key.toLowerCase() === trimmed.toLowerCase()) {
      return key;
    }
  }

  // Substring match — bidirectional
  const lower = trimmed.toLowerCase();
  for (const key of sortedKeys) {
    const lowerKey = key.toLowerCase();
    if (lower.includes(lowerKey) || lowerKey.includes(lower)) {
      return key;
    }
  }

  return null;
}
